/* eslint-disable camelcase */
import { toast } from 'react-toastify';
import { genericErrorMessage } from 'app/store/briisk/actions/formAction';
import axios from 'axios';
import { parameters } from 'api/parameter';
import { productPriceRequestBody } from './ProductOptionHelper';
import { getFactors, getRisks } from './CreatePolicyHelper';
import store from 'app/store';
import { resumePolicyPathNames } from './ResumePolicyHelper';
import { ENDORSABLE_FLAG } from 'utils/enums';
import { getDisplayOneOptionAtTimeDetails } from './GenericHelper';

export const isRiskExcluded = (channelProducts, productInstanceId, productOptionInstanceId, riskPageNo) => {
	const selectedProduct = channelProducts?.value?.find(prod => prod.product.instanceId === productInstanceId);
	const selectedProductOptionExcludedRisks = selectedProduct?.options
		?.find(x => x?.option?.instanceId === productOptionInstanceId)
		?.option?.excludedRisks?.split(',');
	const currentRiskPage = selectedProduct?.risks[riskPageNo - 1];
	const isExcludedRisk = selectedProductOptionExcludedRisks?.includes(currentRiskPage?.code);
	return isExcludedRisk;
};

export const getRiskByRiskIndex = (currentRiskIndex, channelProducts, productId, source = 1) => {
	const { formValues } = store.getState().fuse.form;
	const selectedProduct = channelProducts?.value?.find(prod => prod.product.instanceId === productId);
	// Onboarding
	if (source === 1) {
		return selectedProduct?.risks[currentRiskIndex];
	}
	// Claims
	else if (source === 2) {
		return selectedProduct?.claims.find(claims => claims.instanceId === formValues.claimFlowInstanceId)?.claimForm[
			currentRiskIndex
		];
	} else {
		return {};
	}
};

export const getFactorHeaders = (channelProducts, productId) => {
	let factorHeadingText = '';
	let factorDescriptionText = '';
	const specificProductContent = channelProducts?.value?.find(
		prod => prod.product.instanceId === productId
	)?.contents;
	factorHeadingText = specificProductContent?.find(content => content.key === 'FACTORS-HEADING-TEXT')?.value;
	factorDescriptionText = specificProductContent?.find(content => content.key === 'FACTORS-DESCRIPTION-HTML')?.value;
	return { factorHeadingText, factorDescriptionText };
};

// Gives the current source required details
// Source - 1 = Onboarding, 2 = Claims
export const getCurrentSourceDetails = ({ source, channelProducts, formValues }) => {
	const details = {
		addFirstRiskName: '',
		risks: []
	};

	const selectedProduct = channelProducts?.find(e => e?.product?.instanceId === formValues?.productInstanceId);
	const selectedClaimFlow = selectedProduct?.claims?.find(e => e?.instanceId === formValues?.claimFlowInstanceId);

	// Onboarding
	if (source === 1) {
		details.addFirstRiskName = selectedProduct?.risks[`${formValues?.riskPageNo - 1}`]?.name;
		details.risks = selectedProduct ? [...(selectedProduct?.risks || [])] : [];
	}
	// Claims
	else if (source === 2) {
		details.addFirstRiskName = selectedClaimFlow?.claimForm[`${formValues?.riskPageNo - 1}`]?.name;
		details.risks = selectedClaimFlow ? [...(selectedClaimFlow?.claimForm || [])] : [];
	}

	return { ...details };
};

// To figure out when to show the current risk.
// For instance, we might display Assessment Risks when the underwriter is reviewing a referred policy.
// For Benefit or Cover Risks, we'd show them only during an endorsement process.
export const canCurrentRiskBeDisplayed = ({
	draftPolicy,
	resumePolicyPathNames,
	currentRiskFormType,
	currentRiskRiskType
}) => {
	// FormType = 2 is Assessment Form Risk.
	if (currentRiskFormType === 2) {
		// We have to display Assessment Form Risk only during under writer continuing referred policy.
		if (draftPolicy?.draftType === resumePolicyPathNames.resumeUWCReferredPolicy) {
			return true;
		} else {
			return false;
		}
	}

	// RiskType = 1 is Cover Risk & RiskType = 2 is Benefit Risk.
	if (currentRiskRiskType === 1 || currentRiskRiskType === 2) {
		// We have to display Cover Risk or Benefit Risk only during under writer continuing endorsement.
		if (draftPolicy?.draftType === resumePolicyPathNames.resumeUWCEndorsement) {
			return true;
		} else {
			return false;
		}
	}

	return true;
};

// #region Setting up Initial Values

// To get initial Factor's / Risk's Default Values...
// Note - Should be called only from QuickQuote page.
export const getInitialDefaultValues = (prefixName, currentAttribute, values, phoneCodeDefaultValue) => {
	let newValues = {};
	// Assigning default values...
	if (currentAttribute?.defaultValue) {
		if (!values[`${prefixName}-${currentAttribute?.name}`]) {
			newValues = { ...newValues, [`${prefixName}-${currentAttribute?.name}`]: currentAttribute?.defaultValue };
		}
	}
	// Assigning default phone code...
	if (currentAttribute?.dataType === 13 || currentAttribute?.dataType === 14) {
		if (!values[`${prefixName}-${currentAttribute?.name}Code`]) {
			newValues = {
				...newValues,
				[`${prefixName}-${currentAttribute?.name}Code`]: phoneCodeDefaultValue || '27'
			};
		}
	}
	return newValues;
};

// To get Risk's Default Values...
// Note - Should be called only from Risk Info page.
export const getRisksDefaultValues = (prefixName, currentRiskRisks, values) => {
	const currentRisk = currentRiskRisks[prefixName];
	// Getting phone number's default code...
	const phoneCodeDefaultValue = currentRisk?.attributes?.find(e => e.dataType === 12)?.defaultValue;
	let newValues = {};
	currentRisk?.attributes?.forEach(attribute => {
		// Assigning default values...
		if (attribute?.defaultValue) {
			if (!values[`${prefixName}-${attribute?.name}`]) {
				newValues = { ...newValues, [`${prefixName}-${attribute?.name}`]: attribute?.defaultValue };
			}
		}
		// Assigning default phone code...
		if (attribute?.dataType === 13 || attribute?.dataType === 14) {
			if (!values[`${prefixName}-${attribute?.name}Code`]) {
				newValues = { ...newValues, [`${prefixName}-${attribute?.name}Code`]: phoneCodeDefaultValue || '27' };
			}
		}
	});
	return newValues;
};

// #endregion

// #region Handling Dependencies & Linked Fields

export const getRiskCodeFromAttributeCode = attributeCode => {
	const riskCode = attributeCode?.split('-')?.slice(0, -1)?.join('-');
	return riskCode;
};

// If we would have to get value only from factors, do not pass first param. Pass null.
export const getAttributeValueByCode = (prefix, attributeCode, values, risks, factors) => {
	if (!attributeCode) {
		return {
			value: null
		};
	}
	// values is handed to getFactors and getRisks unwrapped. Both only read from it, while every call
	// here used to build a fresh { ...values }, shallow copying the entire form value bag - one key per
	// attribute of every instance of every risk - twice per call, on a path that runs once per
	// dependency item per attribute per instance on every keystroke.
	// Factors...
	const factorAttribute = getFactors(values, factors)?.find(y => y.code === attributeCode);
	// Returned before the risks are projected below. A found factor is an object, so it always won the
	// ?? chain this function used to end with, which made projecting the risks work that was discarded.
	if (factorAttribute) return factorAttribute;
	// #region Risks
	const riskCode = getRiskCodeFromAttributeCode(attributeCode);
	// Projected once and shared by both lookups below. This used to call getRisks twice, and each call
	// walks every instance of every risk; both uses only read, so one array is safe to share.
	// getRisks iterates without a guard and this is reached from render, hence the ?? [].
	const sameCodeRisks = getRisks(values, risks ?? [], null)?.filter(x => x.code === riskCode);
	// Getting risk by riskCode and by it's any index...
	const risksRiskByIndex =
		sameCodeRisks?.[Number(prefix?.split('-')[2])]?.attributes?.find(y => y.code === attributeCode) || null;
	// Getting risk by riskCode and by it's 1st index...
	// [0] of the same-code list is the element find() returned before this was de-duplicated.
	const risksRiskByFirstIndex = sameCodeRisks?.[0]?.attributes?.find(y => y.code === attributeCode);
	// #endregion
	// A nullish return means "this attribute could not be resolved at all", which callers must not
	// confuse with "resolved and empty" - getRiskDependencyCountTarget turns on that difference.
	return risksRiskByIndex ?? risksRiskByFirstIndex;
};

const compareDependencies = (prefix, dependencies, values, risks, factors) => {
	let isDisabled = true;
	dependencies?.forEach(dependency => {
		// The destination value that we have to compare with.
		const dependentAttribute = getAttributeValueByCode(prefix, `${dependency?.name?.code}`, values, risks, factors);
		const dependentOnValue = dependentAttribute?.value;

		switch (dependency?.rule?.type) {
			// Do the comparison only when the isDisabled is true.
			// Because as per the requirement, we have to perform only OR condition.
			// So that, once the isDisabled becomes false, do not check any other conditions.
			case 1: // ==
				if (isDisabled) {
					dependentOnValue?.toString()?.toLowerCase() === dependency?.value?.toString()?.toLowerCase()
						? (isDisabled = false)
						: (isDisabled = true);
				}
				break;
			case 2: // <
			case 3: // >
			case 4: // <=
			case 5: // >=
				if (isDisabled && [2, 3, 4, 7, 24]?.includes(dependentAttribute?.dataType)) {
					// Getting the dependent's actual values from the formValues Ex. Annual Turnover - R 0 - 1000000
					let dependentNum = Number(dependentOnValue);

					// Getting the dependency's value which is configured from the portal Ex. R 0 - 1000000
					let dependencyNum = Number(dependency?.value);

					// As per the requirements, we need to handle dependency comparisons only when the dependent’s value is not numerical.
					// In such cases, we must parse and use the external numeric values configured at the dependency level to perform the comparison.
					// For example, if the dependent attribute is of List type (e.g., Annual Turnover) and the value is "R 0 - 10000", it cannot be directly compared numerically.
					// So, we look into the dependencies JSON, map the selected value to its corresponding numeric value from the 'external' array,
					// and use that numeric value for further rule evaluations using operators like <, >, <=, >=.

					// If the dependentOnValue is not a number, then we have to parse the dependency's external value to get the dependentNum for comparsion.
					if (isNaN(dependentNum)) {
						// Parse the dependency's JSON to get the dependent details (rules, dependencies, items, and external values).
						const dependencyAttribute = dependency?.name?.dependencies
							? JSON.parse(dependency?.name?.dependencies || '{}')
							: null;

						// If the items and external present, then getting the external from finding the index of item, and matcing with external array.
						// Ex. items - array of dependent's list options, and external - array of external numerical value according to dependent's list.
						if (dependencyAttribute?.items && dependencyAttribute?.external) {
							// Find the index of the item in the items array that matches the dependentOnValue.
							const itemIndex = dependencyAttribute?.items?.findIndex(item => item === dependentOnValue);
							if (itemIndex !== -1) {
								// Updating the external to the dependentNum -  Ex. 100000
								dependentNum = Number(dependencyAttribute?.external[itemIndex]);

								// Updating the dependencyNum to the dependency's external value - Ex. "100000"
								dependencyNum = Number(dependency?.external);
							}
						}
					}

					const ruleType = dependency?.rule?.type;
					isDisabled = !(
						(ruleType === 2 && dependentNum < dependencyNum) ||
						(ruleType === 3 && dependentNum > dependencyNum) ||
						(ruleType === 4 && dependentNum <= dependencyNum) ||
						(ruleType === 5 && dependentNum >= dependencyNum)
					);
				}
				break;
			case 6: // !=
				if (isDisabled) {
					dependentOnValue?.toString()?.toLowerCase() !== dependency?.value?.toString()?.toLowerCase()
						? (isDisabled = false)
						: (isDisabled = true);
				}
				break;
			default:
				break;
		}
	});
	return isDisabled;
};

// Parses a risk's own "dependencies" JSON into its items array.
// The portal sends three different "nothing configured" encodings for this field - the string "null",
// a real null, and '{"items":[]}' - so every one of them has to collapse to an empty result here.
// The parse is wrapped because the value is portal-authored text and this runs during render (the Add
// button's disabled prop). There is no error boundary in the app, so an uncaught SyntaxError would
// unmount the whole tree; failing to an empty list instead leaves the button enabled.
const getRiskDependencyItems = riskConfig => {
	if (!riskConfig?.dependencies) return [];
	let parsed;
	try {
		parsed = JSON.parse(riskConfig.dependencies);
	} catch (ex) {
		console.warn(`Invalid dependencies JSON on risk ${riskConfig?.code}. Ignoring it.`, ex);
		return [];
	}
	// A dependency pointing at an attribute of its own risk cannot be honoured: removing the risk's
	// instances would delete the very field the condition reads, leaving it unresolvable forever.
	return (parsed?.items ?? []).filter(item => getRiskCodeFromAttributeCode(item?.name?.code) !== riskConfig?.code);
};

// Configuration problems reported from the functions below are static - the same risk would log on
// every render of every Add button - so each distinct one is announced once per session.
const warnedRiskDependencyConfigs = new Set();
const warnOnceForRisk = (key, message) => {
	if (warnedRiskDependencyConfigs.has(key)) return;
	warnedRiskDependencyConfigs.add(key);
	console.warn(message);
};

// The portal encodes "no value configured" as null, undefined, an empty string or whitespace. An
// item like that cannot be a comparison - there is nothing to compare against - so it is read as a
// count driver instead. The four-character string "null" is deliberately NOT treated as blank: that
// is a legitimate equality test against the literal text.
const isBlankDependencyValue = item =>
	item?.value === null || item?.value === undefined || `${item?.value}`.trim() === '';

// A count driver: the number held by the attribute at name.code is how many instances of the risk
// must exist. name.code has to be non-empty, because getAttributeValueByCode returns a truthy
// { value: null } for a falsy code, which would otherwise read as "resolved and empty" and clear
// every instance of the risk.
const isCountDriverItem = item => isBlankDependencyValue(item) && !!item?.name?.code;

// Derived once per distinct configuration and cached, because that configuration is a fixed input for
// as long as the app is running. Without this the parse and its three array walks repeat for every
// reader on every render of every Add button, Remove button and tooltip, and a malformed blob logs its
// warning on each of them rather than once - the same reason the warnings above are announced once.
// The parsed items are only ever read, so one partition per configuration is safe to share.
const riskDependencyPartitionCache = new Map();

// Splits a risk's items into the count driver(s) and the ordinary valued conditions.
// Only the valued ones may reach compareDependencies: given a blank value its case 1 collapses to
// `dependentOnValue === undefined`, which reports satisfied exactly when the attribute is empty, and
// OR semantics would then latch that verdict for the whole risk. Case 6 is wrong in mirror image.
const partitionRiskDependencyItems = riskConfig => {
	// Both halves of the key matter: the items come out of the dependencies JSON, and which of them
	// survive depends on the risk's own code. Only string configuration is cached - getRiskDependencyItems
	// JSON.parses the field, so any other shape resolves to an empty list without doing real work.
	const cacheKey =
		typeof riskConfig?.dependencies === 'string' ? `${riskConfig?.code}|${riskConfig.dependencies}` : null;
	if (cacheKey !== null && riskDependencyPartitionCache.has(cacheKey)) {
		return riskDependencyPartitionCache.get(cacheKey);
	}
	const items = getRiskDependencyItems(riskConfig);
	const partition = {
		items,
		driverItems: items.filter(isCountDriverItem),
		gateItems: items.filter(item => !isBlankDependencyValue(item))
	};
	if (cacheKey !== null) riskDependencyPartitionCache.set(cacheKey, partition);
	return partition;
};

// Pulls the parallel items/external arrays out of an embedded attribute definition, used to turn a
// worded list option into the number behind it. compareDependencies reads that pair off
// name.dependencies while the input renderers read a list's options off attribute.settings, so both
// shapes are tried. Portal-authored text parsed on a render path with no error boundary above it,
// hence the try/catch - same reasoning as getRiskDependencyItems.
const parseItemsAndExternal = source => {
	if (!source) return null;
	try {
		const parsed = typeof source === 'string' ? JSON.parse(source) : source;
		return Array.isArray(parsed?.items) && Array.isArray(parsed?.external)
			? { items: parsed.items, external: parsed.external }
			: null;
	} catch (ex) {
		return null;
	}
};

// How many instances of this risk the configuration currently asks for.
//   null   - not count driven, or the driving attribute cannot be resolved yet. Callers must fall
//            back to the ordinary gate behaviour and leave any existing instances alone.
//   number - this risk must have exactly this many instances.
// Takes the already-partitioned items rather than the risk, so that a caller which also needs the
// partition or the satisfied verdict pays for the parse and the value projection only once.
const readCountTarget = ({ riskConfig, driverItems, gateItems, values, risks, factors }) => {
	if (!driverItems.length) return null;
	// The engine ORs its items, so a count has no meaning alongside a second count or a comparison.
	// The first driver wins and the rest is reported rather than silently applied.
	if (driverItems.length > 1 || gateItems.length) {
		warnOnceForRisk(
			`${riskConfig?.code}-mixed-count-dependency`,
			`Risk ${riskConfig?.code} mixes a valueless dependency with other conditions. Only the first valueless item drives the instance count; the rest are ignored.`
		);
	}
	const driver = driverItems[0];
	const driverAttribute = getAttributeValueByCode(null, driver?.name?.code, values, risks, factors);
	// Nullish means the attribute is not in the projection at all, i.e. its own risk page does not
	// exist yet. That is not the same as the user leaving it empty and must not be read as zero:
	// doing so lets two risks that drive each other deadlock, both permanently un-addable, and would
	// delete a risk merely because its driver's page has not been created.
	if (driverAttribute === null || driverAttribute === undefined) return null;
	const rawValue = driverAttribute?.value;
	// Resolved but empty is a real answer: no selection means no instances.
	if (rawValue === null || rawValue === undefined || `${rawValue}`.trim() === '') return 0;
	let count = Number(`${rawValue}`.trim());
	if (!Number.isFinite(count)) {
		// A list can label its options with words and carry the number in the parallel external
		// array, which is the same remap compareDependencies performs for its ordered rules.
		const optionSource =
			parseItemsAndExternal(driver?.name?.dependencies) ?? parseItemsAndExternal(driver?.name?.settings);
		const optionIndex = optionSource?.items?.findIndex(item => item === rawValue) ?? -1;
		count = optionIndex >= 0 ? Number(optionSource?.external?.[optionIndex]) : NaN;
	}
	if (!Number.isFinite(count)) {
		warnOnceForRisk(
			`${riskConfig?.code}-non-numeric-count`,
			`Risk ${riskConfig?.code} is driven by ${driver?.name?.code}, whose value is not a number and has no numeric external mapping. Treating it as 0 instances.`
		);
		return 0;
	}
	count = Math.max(0, Math.trunc(count));
	// maxCount is what the Add button has always enforced, so a driver cannot exceed it either.
	const maxCount = Number(riskConfig?.maxCount);
	if (Number.isFinite(maxCount) && maxCount > 0 && count > maxCount) {
		warnOnceForRisk(
			`${riskConfig?.code}-count-over-max`,
			`Risk ${riskConfig?.code} is driven to ${count} instances but its maxCount is ${maxCount}. Clamping to maxCount.`
		);
		return maxCount;
	}
	return count;
};

// The count on its own, for callers that need nothing else: the reconciler and the predicate below.
export const getRiskDependencyCountTarget = (riskConfig, values, risks, factors) => {
	const { driverItems, gateItems } = partitionRiskDependencyItems(riskConfig);
	return readCountTarget({ riskConfig, driverItems, gateItems, values, risks, factors });
};

// One evaluation of a risk's own dependency, shared by the three readers below. Each needs an
// overlapping part of it, and resolving a count driver costs a projection of the whole form value
// bag, which the readers used to repeat: asking whether the risk was satisfied and then asking for
// its count resolved the same driving attribute twice, and the tooltip asked for both again.
const evaluateRiskDependency = (riskConfig, values, risks, factors) => {
	const { driverItems, gateItems } = partitionRiskDependencyItems(riskConfig);
	const countTarget = readCountTarget({ riskConfig, driverItems, gateItems, values, risks, factors });
	// Defaults to satisfied, which covers both cases that must behave exactly as an unconfigured risk:
	// nothing configured at all, and a list whose only items are unusable. compareDependencies starts
	// from isDisabled = true and its loop never runs on an empty list, so calling it in either case
	// would report "disabled" and pin the button behind a condition that can never be evaluated.
	let isSatisfied = true;
	if (countTarget !== null) {
		// A count driven risk is available whenever the configuration asks for at least one instance. A
		// null target instead means the driver is not resolvable yet, so the valued items decide below.
		isSatisfied = countTarget > 0;
	} else if (gateItems.length) {
		// prefix is null because a risk-level dependency belongs to the risk page rather than to one
		// instance of it; that makes getAttributeValueByCode resolve against the target risk's first
		// instance, which is what a cross-risk condition needs.
		// Only valued items can be compared - see partitionRiskDependencyItems.
		// compareDependencies returns isDisabled, hence the negation.
		isSatisfied = !compareDependencies(null, gateItems, values, risks, factors);
	}
	return { driverItems, gateItems, countTarget, isSatisfied };
};

// Whether the instance count of this risk is decided by configuration rather than by the user.
// The Add and Remove buttons are disabled for these risks, because reconcileCountDrivenRisks
// rewrites the count on the next change and would discard anything added or removed by hand.
export const isRiskCountDriven = riskConfig => partitionRiskDependencyItems(riskConfig).driverItems.length > 0;

// Every form value key family an instance's attribute writes. The value, the disabled flag, and the
// phone-code, lookup-input and lookup-options companions all have to go together: pricing decides
// which risks to send by looking for these keys, and the default-value helpers only write a key when
// it is absent, so a leftover would reappear inside a "new" instance on re-add.
const deleteRiskAttributeKeys = (bag, prefix, attribute) => {
	delete bag[`${prefix}-${attribute.name}`];
	delete bag[`${prefix}-${attribute.name}-disable`];
	delete bag[`${prefix}-${attribute.name}Code`];
	delete bag[`${prefix}-${attribute.name}-input-value`];
	delete bag[`${prefix}-${attribute.name}-dd-options`];
};

// The `j` in a `risks-${i}-${j}` prefix, i.e. which instance of the page it identifies.
const getInstanceIndexFromPrefix = prefix => Number(prefix?.split('-')[2]);

// Maps a dependency rule type onto readable text for the Add button's tooltip.
// The ids are the same ones compareDependencies switches on above.
const dependencyRulePhrase = {
	1: 'is',
	2: 'is less than',
	3: 'is more than',
	4: 'is at most',
	5: 'is at least',
	6: 'is not'
};

// Whether a risk may be added, based on the risk's OWN dependencies (not its attributes').
// Returns true when nothing is configured, so risks without dependencies behave exactly as before.
export const isRiskDependencySatisfied = (riskConfig, values, risks, factors) =>
	evaluateRiskDependency(riskConfig, values, risks, factors).isSatisfied;

// Builds the reason shown on a disabled Add button, entirely from the configured dependency,
// so no attribute name or value is hardcoded. Returns '' when there is nothing to explain.
export const getRiskDependencyMessage = (riskConfig, values, risks, factors) => {
	const { driverItems, gateItems, countTarget, isSatisfied } = evaluateRiskDependency(
		riskConfig,
		values,
		risks,
		factors
	);
	// A count driven risk always carries a message, even while its dependency is satisfied, because
	// its Add and Remove buttons stay disabled either way and a dead button with no explanation is
	// worse than no button. The two cases read differently: nothing chosen yet, versus the number of
	// instances is not the user's to pick.
	if (countTarget !== null) {
		const driverName = driverItems[0]?.name?.name ?? driverItems[0]?.name?.code;
		if (countTarget === 0) return `Available when ${driverName} is set`;
		return `${driverName} sets how many ${riskConfig?.name ?? 'of these'} are added`;
	}
	if (isSatisfied) return '';
	// Only the valued items are described; a valueless one is a count driver handled above, and
	// rendering it here would read as "is undefined".
	const clauses = gateItems.map(
		item =>
			`${item?.name?.name ?? item?.name?.code} ${
				dependencyRulePhrase[item?.rule?.type] ?? item?.rule?.name
			} ${item.value}`
	);
	if (!clauses.length) return 'Not available with your current selections';
	// compareDependencies short-circuits on the first satisfied item and never reads the configured
	// operator, so multiple items are always OR - joining with "and" would misdescribe the rule.
	return `Available when ${clauses.join(' or ')}`;
};

// Clears every instance of any risk whose own dependency is no longer satisfied, so the flow can
// never carry - or price, or submit - a risk the configuration says should not exist.
export const removeRisksWithUnsatisfiedDependencies = ({ values, risks, factors }) => {
	// Snapshot used for every dependency evaluation below, so that pruning one risk cannot change the
	// verdict for a risk evaluated later in the same pass. getRisks() also iterates without a guard,
	// so this doubles as the undefined guard.
	const safeRisks = [...(risks ?? [])];
	const nextRisks = [...safeRisks];
	const nextValues = { ...values };
	const removedPrefixes = [];

	nextRisks.forEach((riskPage, index) => {
		// Already empty pages have nothing to clear.
		if (!riskPage) return;
		const prefixes = Object.keys(riskPage);
		// Every instance of a page is a copy of the same risk definition, so the first one carries
		// the dependency for all of them.
		const riskDefinition = riskPage[prefixes[0]];
		if (isRiskDependencySatisfied(riskDefinition, values, safeRisks, factors)) return;
		// A risk with minCount >= 1 and an unsatisfied dependency is contradictory config. The
		// dependency wins, because the per-instance Remove button is hidden at or below minCount and
		// the Add button is now disabled, which would otherwise leave a page that can be neither
		// completed nor emptied.
		if (riskDefinition?.minCount >= 1) {
			console.warn(
				`Risk ${riskDefinition?.code} has minCount ${riskDefinition?.minCount} but its dependency is not satisfied. Removing its instances.`
			);
		}
		prefixes.forEach(prefix => {
			removedPrefixes.push(prefix);
			riskPage[prefix]?.attributes?.forEach(att => {
				deleteRiskAttributeKeys(nextValues, prefix, att);
			});
		});
		// Assigned by index, never spliced out: the index is the `i` in every `risks-${i}-${j}` form
		// value key, and risks.length gates step advancement, so both must stay unchanged.
		nextRisks[index] = null;
	});

	return {
		values: nextValues,
		risks: nextRisks,
		removedPrefixes,
		hasRemovedRisks: removedPrefixes.length > 0
	};
};

// Sizes every count driven risk to the number its driving attribute asks for: creates instances when
// that number goes up, clears them when it goes down.
// Must run after removeRisksWithUnsatisfiedDependencies and on that function's output, so the two
// never contend for the same page - a risk whose gate is unsatisfied has already been emptied there,
// and a count driven risk asking for 0 instances reports as unsatisfied so the pruner owns it.
// riskConfigs is the channel product's index-parallel risks array, needed because a risk sitting at
// zero instances is a hole in `risks` with no definition of its own left to read.
export const reconcileCountDrivenRisks = ({ values, risks, factors, riskConfigs }) => {
	// Snapshot every target is evaluated against, so that sizing one risk cannot change the verdict
	// for a risk visited later in the same pass. getRisks() also iterates without a guard, so this
	// doubles as the undefined guard.
	const safeRisks = [...(risks ?? [])];
	const nextRisks = [...safeRisks];
	const nextValues = { ...values };
	const addedPrefixes = [];
	const removedPrefixes = [];

	// Bounded by the existing array, never by riskConfigs: writing past the end would grow
	// risks.length, and that length gates step advancement and is `[]` before init - the claims flow
	// fills the same array from an entirely different config list.
	safeRisks.forEach((riskPage, index) => {
		const prefixes = riskPage ? Object.keys(riskPage) : [];
		// An existing page carries its own copy of the definition, which is what holds the draft's id
		// and riskInstanceId; an empty page has none, so fall back to the channel product config.
		const riskDefinition = prefixes.length ? riskPage[prefixes[0]] : riskConfigs?.[index];
		// Plain string truthiness before any parsing. This skips every risk that configures no
		// dependency at all, which is all of them on every product that does not use the feature.
		if (!riskDefinition?.dependencies) return;
		// Evaluated against the untouched snapshot, not the accumulating copy, so that the result is
		// independent of the order the pages happen to be visited in.
		const target = getRiskDependencyCountTarget(riskDefinition, values, safeRisks, factors);
		// Not count driven, or the driving attribute is not resolvable yet - leave the page alone.
		if (target === null) return;
		// Sorted by the numeric suffix rather than left in insertion order: getAttributeValueByCode
		// resolves an instance by indexing that suffix into a list built from Object.keys, so keys
		// out of order would make every attribute-level dependency on the page read a different
		// instance than the one being rendered.
		const sortedPrefixes = [...prefixes].sort(
			(a, b) => getInstanceIndexFromPrefix(a) - getInstanceIndexFromPrefix(b)
		);
		if (sortedPrefixes.length === target) return;

		// Shrinking: the highest numbered instances go first, mirroring the per-instance Remove
		// button, so the instances the user filled in earliest are the ones that survive.
		if (sortedPrefixes.length > target) {
			const survivingPrefixes = sortedPrefixes.slice(0, target);
			sortedPrefixes.slice(target).forEach(prefix => {
				removedPrefixes.push(prefix);
				riskPage[prefix]?.attributes?.forEach(att => {
					deleteRiskAttributeKeys(nextValues, prefix, att);
				});
			});
			// Rebuilt rather than mutated - store state is frozen - and nulled by index assignment
			// when nothing survives, because the index is the `i` in every form value key.
			nextRisks[index] = survivingPrefixes.length
				? Object.fromEntries(survivingPrefixes.map(prefix => [prefix, riskPage[prefix]]))
				: null;
			return;
		}

		// Growing. doNotDisplay is honoured on this side only: the draft restore path can legitimately
		// recreate instances of a hidden risk, and skipping those while shrinking above would leave
		// such a page impossible to clear.
		if (riskDefinition?.doNotDisplay) return;
		const template = prefixes.length ? riskPage[sortedPrefixes[sortedPrefixes.length - 1]] : riskConfigs?.[index];
		if (!template) {
			warnOnceForRisk(
				`${riskDefinition?.code}-no-template`,
				`Risk ${riskDefinition?.code} asks for ${target} instances but no definition is available to build them from. Leaving it empty.`
			);
			return;
		}
		// A new object, so the surviving instances can be carried over and the new ones appended to it
		// in place; the store's frozen page is only read from.
		const nextPage = Object.fromEntries(sortedPrefixes.map(prefix => [prefix, riskPage[prefix]]));
		// Continues past the highest existing suffix instead of filling gaps left by a manual removal:
		// the prefix is the identity of an instance's form values, so reusing one would resurrect the
		// values of the instance that used to hold it.
		let nextInstanceIndex = sortedPrefixes.length
			? getInstanceIndexFromPrefix(sortedPrefixes[sortedPrefixes.length - 1]) + 1
			: 0;
		for (let count = sortedPrefixes.length; count < target; count += 1) {
			const prefix = `risks-${index}-${nextInstanceIndex}`;
			// Each instance gets its own object: id and riskInstanceId are written per instance when
			// the policy is saved, so sharing one reference would make them indistinguishable.
			const instance = { ...template, id: null, riskInstanceId: null };
			// Appended in ascending suffix order, which is the order the rest of the app relies on.
			nextPage[prefix] = instance;
			// Seeded against the values accumulated so far, because getRisksDefaultValues only writes
			// a key it does not already find. It reads a single entry, so it is handed a one-entry page
			// rather than the whole page being copied again for each instance.
			Object.assign(nextValues, getRisksDefaultValues(prefix, { [prefix]: instance }, nextValues));
			addedPrefixes.push(prefix);
			nextInstanceIndex += 1;
		}
		nextRisks[index] = nextPage;
	});

	return {
		values: nextValues,
		risks: nextRisks,
		addedPrefixes,
		removedPrefixes,
		// Derived from what actually changed, never from array identity: setRisks replaces the array
		// unconditionally, so an identity comparison would report a change on every single call and
		// any caller that dispatched on it from an effect would spin forever.
		hasChangedRisks: addedPrefixes.length > 0 || removedPrefixes.length > 0
	};
};

export const handleDynamicFieldsChange = ({ values, risks, factors, attachments = null }) => {
	const draftType = store.getState().fuse.form.draftPolicy.draftType;
	const displayOneOptionAtATime = getDisplayOneOptionAtTimeDetails(values?.productInstanceId);
	// Loop through the factors and disabling the attributes if needed
	factors &&
		Object.keys(factors || {}).forEach(prefix => {
			const clonedAttributes = factors[prefix] ? [...(factors[prefix] || [])] : [];
			clonedAttributes?.forEach(clonedAttribute => {
				// This if statement will execute only if the draftType is resumeUWCEndorsement.
				// It will disable the field if the factor or clonedAttribute endorsable is READ_ONLY.
				if (draftType === resumePolicyPathNames.resumeUWCEndorsement) {
					if (clonedAttribute?.endorsable === ENDORSABLE_FLAG.READ_ONLY) {
						values[`${prefix}-${clonedAttribute?.name}-disable`] = true;
					}
				}
			});
		});

	risks?.forEach(risk => {
		risk &&
			Object.keys(risk).forEach(prefix => {
				const clonedAttributes = risk[prefix] ? [...(risk[prefix]?.attributes || [])] : [];
				clonedAttributes?.forEach(clonedAttribute => {
					// Handling dependencies...
					if (clonedAttribute?.dependencies && JSON.parse(clonedAttribute?.dependencies)?.items?.length > 0) {
						const dependencies = JSON.parse(clonedAttribute?.dependencies)?.items;
						const isDisabled = compareDependencies(prefix, dependencies, values, risks, factors);
						values = { ...values, [`${prefix}-${clonedAttribute?.name}-disable`]: isDisabled };

						// If isDisabled is true, then removing the field's value from the formValues.
						if (isDisabled) {
							values = { ...values, [`${prefix}-${clonedAttribute?.name}`]: null };
						}
					}

					// Handling linked fields...
					if (clonedAttribute?.linkedField) {
						// Disabling the field.
						if (values[`${prefix}-${clonedAttribute?.name}-disable`] !== true) {
							values[`${prefix}-${clonedAttribute?.name}-disable`] = true;
						}
						// Linking the value.
						const linkedOnCode = clonedAttribute?.linkedField?.split('~')[1]?.trim();
						const linkedOnValue = getAttributeValueByCode(
							prefix,
							linkedOnCode,
							values,
							risks,
							factors
						)?.value;
						if (values[`${prefix}-${clonedAttribute?.name}`] !== linkedOnValue) {
							values[`${prefix}-${clonedAttribute?.name}`] = linkedOnValue;
						}
					}

					// Handling disable lookup fields.
					if (clonedAttribute?.lookUps) {
						const { Disabled: disabled } = JSON.parse(clonedAttribute?.lookUps) || {};
						// If Disabled is true from the lookUps, then disabling the field.
						if (disabled) {
							values[`${prefix}-${clonedAttribute?.name}-disable`] = disabled;
						}
					}

					// This if statement will execute only if the draftType is resumeUWCEndorsement.
					// It will disable the field if the risk or clonedAttribute endorsable is READ_ONLY.
					if (draftType === resumePolicyPathNames.resumeUWCEndorsement) {
						if (
							clonedAttribute?.endorsable === ENDORSABLE_FLAG.READ_ONLY ||
							risk[prefix]?.endorsable === ENDORSABLE_FLAG.READ_ONLY
						) {
							values[`${prefix}-${clonedAttribute?.name}-disable`] = true;
						}
					}
				});
				// Handling attachment dependencies...
				attachments?.attachments?.forEach(attachment => {
					if (attachment?.dependencies && JSON.parse(attachment?.dependencies)?.items?.length > 0) {
						const dependencies = JSON.parse(attachment?.dependencies)?.items;
						const isRequired = !compareDependencies(prefix, dependencies, values, risks, factors);
						values = { ...values, [`${attachment.instanceId}-required`]: isRequired };
					}
				});
			});
	});
	// Handling attachments required validations...
	attachments?.attachments?.forEach(attachment => {
		values = { ...values, [`${attachment.instanceId}-required`]: attachment?.isRequired };
	});

	// Handling the default value for displayOneOptionAtATime
	if (displayOneOptionAtATime.displayOneOption && !values?.OneOptionDisplayValue) {
		values = {
			...values,
			OneOptionDisplayValue: Number(displayOneOptionAtATime.defaultValue)
		};
	}
	return { ...values };
};

export const handleDynamicErrorChange = ({ values, errorObj }) => {
	if (!errorObj) return errorObj;
	// eslint-disable-next-line prefer-const
	let clonedErrorObj = { ...errorObj };
	Object?.keys(errorObj).forEach(key => {
		if (values[`${key}-disable`] || values[`${key}-disable-taken-down`]) {
			delete clonedErrorObj[`${key}`];
		}
	});
	return clonedErrorObj;
};

// #endregion

// #region On click next button validations...

export const isRiskAddButtonDisabled = (
	currentRisks_Risks,
	linkedAttributeCode,
	values,
	risks,
	factors,
	riskConfig = null
) => {
	// Checked before anything else: the "Not More Than Linked Attribute" check below returns early
	// for every risk that does not configure one, which is the case for most optional risks, so a
	// dependency check placed after it would never run for them.
	// riskConfig defaults to null so any call site that does not pass it behaves exactly as before.
	// Both facts come from one evaluation, because asking for them separately resolves the driving
	// attribute twice for every count driven risk on every render.
	const { countTarget, isSatisfied } = evaluateRiskDependency(riskConfig, values, risks, factors);
	if (!isSatisfied) return true;
	// A count driven risk takes its instance count from configuration, so adding by hand is never
	// right - reconcileCountDrivenRisks would discard the extra instance on the next change. This
	// also has to sit above the linked-attribute early return below, which fires for every risk that
	// does not configure one and would otherwise skip this check entirely.
	if (countTarget !== null && Object.keys(currentRisks_Risks || {}).length >= countTarget) return true;
	// When the "Not More Than Linked Attribute" is not linked, enable the Add button.
	if (!linkedAttributeCode) return false;
	// When the "Not More Than Linked Attribute" is linked,
	const attributeValue = getAttributeValueByCode(null, linkedAttributeCode, values, risks, factors)?.value;
	// If no value is entered on the linked attribute, always disable the Add button.
	if (!attributeValue) return true;
	// If the entered value on the linked attribute is 0, even then disable the Add button.
	if (parseInt(attributeValue, 10) === 0) return true;
	// Before the Risk gets added for the first time &&
	// When the Risk is linked to "Not More Than Linked Attribute" &&
	// When the user enters the value apart from 0, enable the Add button.
	if (!currentRisks_Risks && linkedAttributeCode && attributeValue) {
		return false;
	}
	// When the count of current Risk's -> Risks is >= the total count
	// of the user entered value on Linked field, disable the Add button.
	if (Object?.keys(currentRisks_Risks)?.length >= parseInt(attributeValue, 10)) return true;
	// else enable it all the time.
	return false;
};

// Returns true, if isNotMoreThanLinkedAttributeLinked is linked.
// Returns false, if isNotMoreThanLinkedAttributeLinked is not linked.
export const isNotMoreThanLinkedAttributeLinked = (channelProducts, productId, currentRiskIndex) => {
	const notMoreThanLinkedAttribute = channelProducts?.value?.find(
		channelProduct => channelProduct?.product?.instanceId === productId
	)?.risks[currentRiskIndex]?.notMoreThanLinkedAttribute;
	return !!notMoreThanLinkedAttribute;
};

// Params,
// currentRiskFromCP = Current Risk from Channel Product.
// currentRisks_Risks = Current Risks of Risk.
// values = All the user entered values.
// risks = All risks.
// factors = All factors.
const validateNotMoreThanHelper = (currentRiskFromCP, currentRisks_Risks, values, risks, factors) => {
	let errorObj = {};
	const notMoreThanLinkedAttribute = currentRiskFromCP?.notMoreThanLinkedAttribute;
	if (notMoreThanLinkedAttribute) {
		// Current Risk's -> Risks count...
		const currentRisks_Risks_Count = Object.keys(currentRisks_Risks || {})?.length;
		// Getting linked field value...
		// As we have to fetch linked value only from factors, hence don't have to pass first param.
		const attributeValue = getAttributeValueByCode(
			null,
			notMoreThanLinkedAttribute?.split('~')[0]?.trim(),
			values,
			risks,
			factors
		)?.value;
		// When the count of current Risk's -> Risks is greater than or lesser than the total count of the user entered value on Linked field...
		if (
			currentRisks_Risks_Count > parseInt(attributeValue, 10) ||
			currentRisks_Risks_Count < parseInt(attributeValue, 10)
		) {
			const errorMessage = `${currentRisks_Risks_Count} ${currentRiskFromCP?.name}(s) are added instead of ${attributeValue}`;
			errorObj = { ...errorObj, [currentRiskFromCP?.name]: [errorMessage] };
			toast.error(`${errorMessage}`, {
				autoClose: 10000
			});
		}
		// When current Risk's -> Risks are added when no value entered on Linked field...
		else if (currentRisks_Risks_Count > 0 && !attributeValue) {
			const errorMessage = `Invalid ${currentRiskFromCP?.name}(s). Either remove all ${
				currentRiskFromCP?.name
			}(s), nor enter correct ${notMoreThanLinkedAttribute?.split('~')[1]?.trim()}`;
			errorObj = { ...errorObj, [currentRiskFromCP?.name]: [errorMessage] };
			toast.error(`${errorMessage}`, {
				autoClose: 10000
			});
		}
	}
	return errorObj;
};

export const validateUniqueAttributesHelper = (currentRiskFromCP, values, risks) => {
	let errorObj = {};
	const risksWithValues = getRisks({ ...values }, risks, null)?.filter(x => x.code === currentRiskFromCP?.code);
	const uniqueAttributes = [...(currentRiskFromCP?.attributes?.filter(x => x.uniqueFlag === 'true') ?? [])];
	uniqueAttributes.forEach((uniqueAttribute, i) => {
		const anArrayOfValues = risksWithValues
			?.map(e => e?.attributes?.filter(x => x?.code === uniqueAttribute?.code)?.map(o => o.value))
			?.map(v => v[0]);

		// Check if all values in the anArrayOfValues are equal to undefined
		// There's no need to validate the attribute if it's null or undefined.
		const allUndefined = anArrayOfValues?.every(item => item === undefined || !item?.length);

		if (!allUndefined) {
			if (anArrayOfValues?.length !== new Set(anArrayOfValues?.map(val => val?.toString()?.toLowerCase())).size) {
				const errorMessage = `${uniqueAttribute?.name} should be unique.`;
				errorObj = { ...errorObj, [`${currentRiskFromCP?.name}-${i}`]: [errorMessage] };
				toast.error(`${errorMessage}`, {
					autoClose: 10000
				});
			}
		}
	});
	return errorObj;
};

export const handleOnClickNextValidation = ({
	channelProducts,
	selectedProductId,
	currentRiskPageIndex,
	isFromRiskInfo,
	values,
	risks,
	factors
}) => {
	let errorObj = {};
	// If this method has been called when the user is on Quick Quote page,
	// then we have to validate all the Risks.
	if (!isFromRiskInfo) {
		risks?.forEach((risk, i) => {
			const currentRiskFromCP = getRiskByRiskIndex(i, channelProducts, selectedProductId);
			if (currentRiskFromCP?.attributes?.find(e => e.quickQuote)) {
				errorObj = {
					...errorObj,
					...validateNotMoreThanHelper(currentRiskFromCP, risk, values, risks, factors)
				};
			}
		});
	}
	// If this method has been called when the user is on Risk Info page,
	// then we have to validate only the current Risk page.
	else {
		const currentRiskFromCP = getRiskByRiskIndex(currentRiskPageIndex, channelProducts, selectedProductId);
		errorObj = {
			...errorObj,
			...validateNotMoreThanHelper(currentRiskFromCP, risks[currentRiskPageIndex], values, risks, factors),
			...validateUniqueAttributesHelper(currentRiskFromCP, values, risks)
		};
	}
	return errorObj;
};

// #endregion

// #region Handling service risk...

export const getExecuteServiceRisksPayload = (risks, formValues, channelProducts, productId, productPriceNew) => {
	// the current risk page.
	const currRisk = { ...[...risks][`${formValues.riskPageNo - 1}`] };
	// selected product.
	const currProduct = channelProducts?.value?.find(
		channelProduct => channelProduct?.product?.instanceId === productId
	);
	// calculated price of all the options.
	const optionPrice = productPriceNew?.value?.productOptions?.filter(
		e => e.productOptionReference === formValues.productOptionInstanceId
	)[0];
	// all the risks page values.
	const risksWithValues = getRisks({ ...formValues }, risks, optionPrice);

	return {
		currRisk,
		currProduct,
		risksWithValues
	};
};

export const handleDependentServiceRiskResponse = (response, formValues) => {
	let serviceRisks = [];
	if (response?.updatedMapServiceRisksDtos) {
		if (formValues?.serviceRisks && formValues?.serviceRisks.length > 0) {
			serviceRisks = [
				// Removing the current Service Risks Info results of the current Service Risk Info page if any
				...(formValues?.serviceRisks
					? formValues.serviceRisks.filter(e => e.code !== response?.updatedMapServiceRisksDtos[0]?.code)
					: []),
				// Adding the updated Service Info Risks results of the current Service Risk Info page
				...(response?.updatedMapServiceRisksDtos || [])
			];
		} else {
			serviceRisks = [...response.updatedMapServiceRisksDtos];
		}
	} else {
		serviceRisks = formValues?.serviceRisks?.length > 0 ? [...formValues.serviceRisks] : [];
	}
	return serviceRisks;
};

// #endregion

const executeServiceRisksCall = requestBody => {
	return new Promise((resolve, reject) => {
		axios
			.post(`${parameters.serviceUrl}/Product/ExecuteServiceRisks`, requestBody)
			.then(result => {
				resolve(result.data);
			})
			.catch(error => {
				reject(error);
			});
	});
};

// Dependent Service Risks are the one which needed a API call to verify/perform something. In return, API will return all the attributes with the values.
// Independent Service Risks are the one which does not need a API call. We just have to assign a value to all the attributes from their linked attribute.
export const executeDependentServiceRisks = async (currentRisk, serviceRisks, risksWithValues) => {
	return new Promise((resolve, reject) => {
		if (!currentRisk || !serviceRisks || serviceRisks.length <= 0) {
			resolve();
			return;
		}
		// if the service risks is three and the second service risk is executing the api call then forEach will not wait for the api, and it's execute the next iteration
		// isAnyServiceRiskExecuting used for restricting the resolve on the else condition
		// THIS IS TEMPORARY FIX, need to enhance the executions of the service risks
		let isAnyServiceRiskExecuting = false;
		serviceRisks.forEach(async (serviceRisk, index) => {
			// if any service risk is linked to the current risk page, then call api.
			if (serviceRisk?.code.split('-')[1] === Object.values(currentRisk)[0].code) {
				try {
					isAnyServiceRiskExecuting = true;
					const result = await executeServiceRisksCall({
						serviceRisk: { ...serviceRisk },
						risksWithValues: [...risksWithValues]
					});
					resolve(result || {});
				} catch (error) {
					reject(error);
				}
			}
			// if no service risk is linked to the current risk page, just continue.
			else if (serviceRisks.length - 1 === index && !isAnyServiceRiskExecuting) {
				resolve();
			}
		});
	});
};

export const enforceServerSideValidation = async (
	enforce = false,
	productsInfo,
	values,
	risks,
	factors,
	selectedProductOptionInstanceId
) => {
	return new Promise((resolve, reject) => {
		if (!enforce) {
			resolve({ isSuccess: true });
			return;
		}
		const requestBody = productPriceRequestBody(productsInfo, risks, factors, values);
		axios
			.post(`${parameters.serviceUrl}/GetProductPriceNew`, requestBody)
			.then(result => {
				if (result?.data) {
					const selectedProductOption = result?.data?.productOptions?.find(
						e => e?.productOptionReference === selectedProductOptionInstanceId
					);
					if (selectedProductOption?.rule?.isValid) {
						resolve({ isSuccess: true });
					} else {
						resolve({
							isSuccess: false,
							message: selectedProductOption?.rule?.message || 'Invalid details.'
						});
					}
				}
			})
			.catch(error => {
				resolve({ isSuccess: false, message: genericErrorMessage });
			});
	});
};
